/*
  assets/rad-video-src.js

  Pick the file a <video> should play. That is the whole job.

  NOTE FOR ANYONE EDITING: this is a .js asset, so Liquid tags are NOT
  processed here. A stray comment tag would be shipped verbatim into
  the file and break it -- which has happened once already.

  WHY THIS REPLACED A STREAMING PLAYER

  The videos on this store loop. That one fact defeats adaptive
  streaming, because a media buffer holds each part of a clip at
  whatever quality it was first fetched: anything that improves
  part-way through leaves a low-quality opening in memory, and a
  looping clip returns to it for ever. Five attempts to keep both
  adaptivity and a clean loop each fixed one symptom and caused
  another -- the history is in snippets/rad-video-quality.liquid.

  So quality was fixed at 720p. And once quality is fixed, an adaptive
  streaming library has nothing left to do: hls.js was carrying a
  100KB download and a loop hitch of its own, in exchange for a
  feature that had been switched off. A plain MP4 plays natively
  everywhere, needs no library, and -- the part that matters -- LOOPS
  SEAMLESSLY, because the browser's own player has always done that
  well.

  WHICH FILE

  Shopify hands us one MP4 in video.sources, always the largest:

    ....HD-1080p-7.2Mbps-94013833.mp4

  The other renditions exist at the same URL with the rendition name
  substituted, which is how the 720p file is found:

    ....HD-720p-4.5Mbps-94013833.mp4     confirmed 200
    ....SD-480p-1.5Mbps-94013833.mp4     confirmed 200

  THAT NAMING IS NOT DOCUMENTED. It is inferred from the URLs Shopify
  produces, and it could change without warning. So the derived URL is
  never trusted: the element gets it, and an `error` on the element
  falls straight back to the 1080p file Shopify actually gave us.
  Worse picture, more data, still plays -- which is the right way
  round for a guess.

  Note the bitrates are not evenly spaced: 720p is 4.5 Mbps, not the
  3.5 a first guess assumed. A URL built on that assumption 404s, and
  that is exactly the sort of thing the fallback is for.

  API
    RadVideoSrc.best(list)       -> { url, fallback } or null
    RadVideoSrc.apply(video, l)  -> sets src, wires the fallback
*/
window.RadVideoSrc = (function(){
  /*
    THE ONE NUMBER. Changing it changes every video on the store --
    tiles, the corner preview, the fullscreen feed, the product page.
    720p is indistinguishable from 1080p on a phone at arm's length and
    costs about 60% of the data; the visible difference is on a large
    desktop monitor.
  */
  var WANT = 'HD-720p-4.5Mbps';
  var LARGEST = 'HD-1080p-7.2Mbps';

  var DEBUG = false;
  function debugOn(){
    try { return sessionStorage.getItem('radDebug') === '1'; } catch(e) { return false; }
  }
  function dbg(){
    if (!debugOn()) { return; }
    var args = ['[rad]'].concat(Array.prototype.slice.call(arguments));
    try { console.log.apply(console, args); } catch(e) {}
  }

  function nameOf(video){
    try {
      if (video.hasAttribute('data-reels-widget-video')) { return 'widget'; }
      var slide = video.closest('.rad-reels__slide');
      if (slide) { return 'slide ' + (slide.dataset.index || '?'); }
      if (video.closest('[data-vrow-tile]')) { return 'tile'; }
      if (video.closest('[data-pdp-video]')) { return 'product'; }
    } catch(e) {}
    return 'video';
  }

  function mp4Of(list){
    for (var i = 0; i < list.length; i++) {
      if (/mp4/i.test(list[i].m)) { return list[i].u; }
    }
    return null;
  }

  return {
    /*
      The wanted rendition and the one to fall back to. Returns null
      when there is no MP4 at all, which should not happen on this
      library but is worth not crashing over.
    */
    best: function(list){
      if (!list || !list.length) { return null; }
      var mp4 = mp4Of(list);
      if (!mp4) { return null; }
      var url = mp4;
      try { url = new URL(mp4, location.href).href; } catch(e) {}
      var wanted = url.indexOf(LARGEST) !== -1 ? url.replace(LARGEST, WANT) : url;
      return { url: wanted, fallback: url };
    },

    /*
      Point the element at the wanted file, and arrange for one
      automatic retry at the size Shopify definitely gave us. The
      listener is `once` and clears itself, so a genuinely broken
      video does not loop between two URLs for ever.
    */
    apply: function(video, list){
      var pick = this.best(list);
      if (!video || !pick) { return false; }
      if (pick.url !== pick.fallback) {
        video.addEventListener('error', function onErr(){
          video.removeEventListener('error', onErr);
          dbg(nameOf(video), '720p missing, falling back to 1080p');
          video.src = pick.fallback;
          try { video.load(); } catch(e) {}
          video.play().catch(function(){});
        }, { once: true });
      }
      video.src = pick.url;
      try { video.load(); } catch(e) {}
      /*
        Reported once per element, because "what is it actually
        playing" was the question behind most of a week of debugging.
      */
      if (!video.__radSrcWatch) {
        video.__radSrcWatch = 1;
        video.addEventListener('loadedmetadata', function(){
          dbg(nameOf(video), 'playing', video.videoHeight + 'p');
        });
        video.addEventListener('waiting', function(){
          dbg(nameOf(video), 'stalled, buffering');
        });
      }
      return true;
    }
  };
})();
